import type {ArrWaveformState} from '../state/arrwaveform-state';
import {effect, type Signal} from './arr-waveform-store';
export type EventEmitter = {emit(event: string, ...args: any[]): void};
export function setupStateEventEmission(
  state: ArrWaveformState,
  emitter: EventEmitter
): () => void {
  const cleanups: Array<() => void> = [];
  cleanups.push(
    effect(() => {
      const isPlaying = state.isPlaying.value;
      emitter.emit(isPlaying ? 'play' : 'pause');
    }, [state.isPlaying])
  );
  cleanups.push(
    effect(() => {
      const currentTime = state.currentTime.value;
      emitter.emit('timeupdate', currentTime);
      if (state.isPlaying.value) {
        emitter.emit('audioprocess', currentTime);
      }
    }, [state.currentTime, state.isPlaying])
  );
  cleanups.push(
    effect(() => {
      const isSeeking = state.isSeeking.value;
      if (isSeeking) {
        emitter.emit('seeking', state.currentTime.value);
      }
    }, [state.isSeeking, state.currentTime])
  );
  let wasReady = false;
  cleanups.push(
    effect(() => {
      const isReady = state.isReady.value;
      if (isReady && !wasReady) {
        wasReady = true;
        emitter.emit('ready', state.duration.value);
      }
    }, [state.isReady, state.duration])
  );
  let wasPlayingAtEnd = false;
  cleanups.push(
    effect(() => {
      const isPlaying = state.isPlaying.value;
      const currentTime = state.currentTime.value;
      const duration = state.duration.value;
      const isAtEnd = duration > 0 && currentTime >= duration;
      if (wasPlayingAtEnd && !isPlaying && isAtEnd) {
        emitter.emit('finish');
      }
      wasPlayingAtEnd = isPlaying && isAtEnd;
    }, [state.isPlaying, state.currentTime, state.duration])
  );
  cleanups.push(
    effect(() => {
      const zoom = state.zoom.value;
      if (zoom > 0) {
        emitter.emit('zoom', zoom);
      }
    }, [state.zoom])
  );
  return () => {
    cleanups.forEach((cleanup) => cleanup());
  };
}
export function setupSignalEventEmission<T>(
  signal: Signal<T>,
  emitter: EventEmitter,
  getEventData: (value: T) => [string, ...any[]]
): () => void {
  return effect(() => {
    const value = signal.value;
    const [eventName, ...args] = getEventData(value);
    emitter.emit(eventName, ...args);
  }, [signal]);
}
export function setupDebouncedEventEmission<T>(
  signal: Signal<T>,
  emitter: EventEmitter,
  getEventData: (value: T) => [string, ...any[]],
  debounceMs: number
): () => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const cleanup = effect(() => {
    const value = signal.value;
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      const [eventName, ...args] = getEventData(value);
      emitter.emit(eventName, ...args);
      timeoutId = null;
    }, debounceMs);
  }, [signal]);
  return () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    cleanup();
  };
}
export function setupConditionalEventEmission<T>(
  signal: Signal<T>,
  emitter: EventEmitter,
  condition: (value: T) => boolean,
  getEventData: (value: T) => [string, ...any[]]
): () => void {
  return effect(() => {
    const value = signal.value;
    if (condition(value)) {
      const [eventName, ...args] = getEventData(value);
      emitter.emit(eventName, ...args);
    }
  }, [signal]);
}
